import { Link } from "react-router-dom";
import { BadgeCheck, ShieldCheck, ExternalLink, Mic, Scale } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { newsroomsByCategory, VERIFIED_NEWSROOMS } from "../lib/verifiedNewsrooms";

export default function Trust() {
  const groups = newsroomsByCategory();

  return (
    <>
      <Header />
      <main className="max-w-[1100px] mx-auto px-5 py-8">
        {/* Hero */}
        <section className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-8 md:p-10" data-testid="trust-hero">
          <div className="inline-flex items-center gap-2 text-[var(--gold)] text-[11px] uppercase tracking-[0.2em] font-black mb-3">
            <ShieldCheck size={14} /> Editorial Trust
          </div>
          <h1 className="text-[36px] md:text-[48px] font-black tracking-[-0.025em] leading-[1.05]">
            How Newseze decides <span className="text-[var(--gold)]">what's trustworthy.</span>
          </h1>
          <p className="text-[15px] md:text-[17px] opacity-95 mt-3 leading-snug max-w-[720px]">
            Newseze aggregates news from {VERIFIED_NEWSROOMS.length}+ verified
            newsrooms — wire services, broadcasters, prestige print, and major
            international outlets. Each one publishes its editorial standards
            publicly. We embed and credit. We never republish their bodies.
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            <span className="inline-flex items-center gap-1 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[12px] font-bold">
              <BadgeCheck size={12} /> {VERIFIED_NEWSROOMS.length} verified sources
            </span>
            <span className="inline-flex items-center gap-1 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[12px] font-bold">
              <Scale size={12} /> No drama. No conspiracies.
            </span>
            <span className="inline-flex items-center gap-1 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[12px] font-bold">
              <Mic size={12} /> AI-scored. Human-respectful.
            </span>
          </div>
        </section>

        {/* Pledge */}
        <section className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6 md:p-8" data-testid="trust-pledge">
          <h2 className="text-[22px] font-black tracking-[-0.02em] text-[var(--ink)]">The Newseze Editorial Pledge</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-[13.5px] text-[var(--ink)]">
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Every story is scored for evidence, balance, drama, and conspiracy before it's shown.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> No drama. No conspiracies. No clickbait headlines. Sensational or fringe scores ≥60 are auto-blocked.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Respect for every public official, regardless of party.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Sources cited on every card. Editorial labeled. Voices labeled. AI-rewritten headlines flagged.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> We embed and credit. We never re-host video or copy article bodies.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Three newsroom avatars carry stories in their lane: Chella (headlines), Michael (finance), Marcus (breaking).</li>
          </ul>
        </section>

        {/* Verified roster, grouped */}
        <section className="mt-10" data-testid="trust-roster">
          <div className="flex items-center gap-2 mb-3">
            <BadgeCheck size={18} className="text-[var(--gold)]" />
            <h2 className="text-[22px] font-black tracking-[-0.02em] text-[var(--ink)]">Verified Newsrooms on Newseze</h2>
          </div>
          <p className="text-[13.5px] text-[var(--muted)] leading-snug mb-6 max-w-[720px]">
            Every name below is an actively-running RSS source on Newseze.
            Click any name to read that newsroom's published editorial
            standards or ethics policy.
          </p>

          <div className="space-y-8">
            {Object.entries(groups).map(([category, items]) => (
              <div key={category} data-testid={`trust-group-${category.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3 pb-2 border-b border-[var(--line)]">
                  {category} <span className="text-[var(--muted)]">· {items.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {items.map((n) => (
                    <a
                      key={n.id}
                      href={n.editorialUrl}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      referrerPolicy="no-referrer"
                      className="group rounded-lg border border-[var(--line)] bg-white hover:border-[var(--gold)] hover:bg-[#fff9ed] transition-colors px-3 py-2.5 flex items-center justify-between gap-2"
                      data-testid={`trust-source-${n.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <BadgeCheck size={14} strokeWidth={2.5} className="text-[var(--gold)] shrink-0" />
                        <span className="text-[14px] font-black text-[var(--ink)] truncate">{n.label}</span>
                      </div>
                      <ExternalLink size={12} className="text-[var(--muted)] group-hover:text-[var(--blue)] shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Closing */}
        <section className="mt-10 rounded-2xl bg-[var(--sky)] border border-[var(--sky-2)] p-6 md:p-8" data-testid="trust-closing">
          <h3 className="text-[20px] font-black tracking-[-0.02em] text-[var(--ink)]">Spot a source we should add or remove?</h3>
          <p className="text-[13.5px] text-[var(--ink)] leading-snug mt-2">
            Newseze welcomes editor feedback. If a newsroom publishes a public
            corrections process and verifiable editorial standards, we'll
            consider it for verification. If a verified source falls below our
            standards, we'll remove it — publicly.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/legal" className="btn" data-testid="trust-read-legal">Read editorial standards</Link>
            <a href="mailto:editor@newseze.com?subject=Newsroom%20verification%20feedback" className="btn primary" data-testid="trust-contact-editor">Email the editor</a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
